import React, { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import PageLoading from 'components/common/PageLoading';
import PlanInformation from 'components/managesubscription/PlanInformation';
import PaymentInformation from 'components/managesubscription/PaymentInformation';
import BillingHistory from 'components/managesubscription/BillingHistory';
import TopNav from 'components/managesubscription/TopNav';
import Page from 'components/common/Page';
import { graphql } from 'react-relay/hooks';
import { ManageSubscriptionQuery } from './__generated__/ManageSubscriptionQuery.graphql';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment } from 'relay-hooks';
import { ExistingPaymentSource } from 'components/payment/PaymentMethodFormBody';
import Subscription from './SBApiSubscriptions';
import Bill from './SBApiBilling';

interface SubscriptionState {
  enrollment: any;
  billingId: string | null;
  billingHistory: any;
  paymentSources: ExistingPaymentSource[];
  isLogoApproved: boolean;
  inactive: boolean;
  viewer: any;
  subscriptionId: any;
}

const ManageSubscription = () => {
  const path = useRouteMatch() as any;
  const enrollmentId = path.params.id;
  const environment = useRelayEnvironment();

  const [isLoading, setLoading] = useState(true);
  const [state, setState] = useState<SubscriptionState>({
    enrollment: null,
    billingId: null,
    billingHistory: null,
    paymentSources: [],
    isLogoApproved: false,
    inactive: false,
    viewer: null,
    subscriptionId: null,
  });

  const enrollmentData = useCallback(() => {
    if (enrollmentId !== '' && enrollmentId !== undefined) {
      fetchQuery<ManageSubscriptionQuery>(
        environment,
        graphql`
          query ManageSubscriptionQuery($enrollmentId: ID!) {
            viewer {
              firstName
              lastName
              contactEmail
              primaryPhoneNumber
              phoneNumbers {
                number
              }
            }
            enrollment: node(id: $enrollmentId) {
              ... on PartnerProgramEnrollment {
                id
                __typename
                partnerProgram {
                  name
                }
                partnerAccount {
                  partnerBillings {
                    edges {
                      node {
                        ... on DigitalRiverBilling {
                          id
                          paymentSources {
                            paymentSourceId
                            clientSecret
                            paymentSessionId
                            payPalBillingToken
                            type
                            creditCard {
                              lastFourDigits
                              expirationMonth
                              brand
                              expirationYear
                            }
                            billTo {
                              firstName
                              lastName
                              organization
                              email
                              address {
                                addressLine1
                                addressLine2
                                city
                                stateProvinceRegionCode
                                zipPostalCode
                                countryCode
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                status
                activationDate
                billingCycle
                enrollmentMarket
                purchaseDate
                cancellationDate
                cancellationReason
                subscriptionEndDate
                billableQuantity
                autoRenew
                ... on HomeownerAppBrandingPartnerProgramEnrollment {
                  logoApproved
                }
                sapSubscriptionDocumentNumber
                paymentSource {
                  paymentSourceId
                  clientSecret
                  paymentSessionId
                  payPalBillingToken
                  type
                  creditCard {
                    lastFourDigits
                    expirationMonth
                    brand
                    expirationYear
                  }
                  billTo {
                    firstName
                    lastName
                    organization
                    email
                    address {
                      addressLine1
                      addressLine2
                      city
                      stateProvinceRegionCode
                      zipPostalCode
                      countryCode
                    }
                  }
                }
                partnerBillingOrders {
                  edges {
                    node {
                      ... on DigitalRiverOrder {
                        id
                        paymentSource {
                          paymentSourceId
                          state
                          type
                          paymentSessionId
                          currency
                          creditCard {
                            lastFourDigits
                            expirationMonth
                            expirationYear
                            brand
                          }
                          payPalBillingToken
                        }
                        lineItems {
                          sku
                          quantity
                          amount {
                            amount
                            currencyCode
                          }
                          billingPeriodStartDate
                          billingPeriodEndDate
                        }
                        invoiceNumber
                        tax {
                          amount
                          currencyCode
                        }
                        subTotal {
                          amount
                          currencyCode
                        }
                        total {
                          amount
                          currencyCode
                        }
                        createdAt
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        {
          enrollmentId,
        }
      ).subscribe({
        next: data => {
          const theViewer = data?.viewer ?? {};
          const theEnrollment = data?.enrollment
            ? { ...data.enrollment }
            : null;
          const billingId =
            theEnrollment?.partnerAccount?.partnerBillings?.edges?.[0]?.node
              ?.id ?? null;

          const sources =
            theEnrollment?.partnerAccount?.partnerBillings?.edges?.[0]?.node
              ?.paymentSources ?? [];

          const theSources: ExistingPaymentSource[] =
            sources?.map(src => {
              const src1 = { ...src };
              const {
                paymentSourceId,
                paymentSessionId,
                clientSecret,
                type,
                payPalBillingToken,
              } = src1;

              const src2 = {
                paymentSourceId,
                paymentSessionId,
                clientSecret,
                type: (type ?? null) as any,
                payPalBillingToken,
                ...(src?.creditCard
                  ? ({ creditCard: { ...src.creditCard } } as any)
                  : null),
                ...(src?.billTo
                  ? {
                      billTo: {
                        ...src.billTo,
                        ...(src.billTo.address
                          ? { address: { ...src.billTo.address } }
                          : null),
                      },
                    }
                  : null),
              };

              return src2;
            }) ?? [];

          setState({
            enrollment: theEnrollment,
            billingHistory: theEnrollment?.partnerBillingOrders?.edges ?? null,
            billingId,
            paymentSources: theSources,
            isLogoApproved: theEnrollment?.logoApproved ?? false,
            inactive: theEnrollment?.status !== 'ACTIVE',
            viewer: theViewer,
            subscriptionId:
              theEnrollment?.sapSubscriptionDocumentNumber ?? null,
          });
          setLoading(false);
        },
        error: () => {
          setState({
            enrollment: null,
            billingHistory: null,
            billingId: null,
            paymentSources: [],
            isLogoApproved: false,
            inactive: false,
            viewer: null,
            subscriptionId: null,
          });
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
      setState({
        enrollment: null,
        billingHistory: null,
        billingId: null,
        paymentSources: [],
        isLogoApproved: false,
        inactive: false,
        viewer: null,
        subscriptionId: null,
      });
    }
  }, [environment, enrollmentId]);

  useEffect(() => {
    enrollmentData();
  }, [enrollmentId]);

  const handleEnrollmentUpdate = (enrollment, sources) => {
    if (sources && sources.length === 0) {
      return;
    }
    setState({
      viewer: { ...state.viewer },
      enrollment: {
        ...state.enrollment,
        ...enrollment,
      },
      billingId: state.billingId,
      billingHistory: enrollment?.partnerBillingOrders?.edges
        ? enrollment?.partnerBillingOrders?.edges
        : state.billingHistory,
      paymentSources: sources ?? state.paymentSources,
      isLogoApproved: enrollment?.logoApproved
        ? enrollment.logoApproved
        : state.inactive,
      inactive: enrollment?.status
        ? enrollment.status !== 'ACTIVE'
        : state.inactive,
      subscriptionId: state.subscriptionId,
    });
  };

  const [paymentPlan, setPaymentPlan] = useState() as any;
  const [serviceLocations, setServiceLocations] = useState() as any;
  const [customerId, setCustomerId] = useState() as any;
  const [monthlyBill, setMonthlyBill] = useState() as any;

  useEffect(() => {
    if (state.subscriptionId) {
      const subscription = new Subscription(state.subscriptionId);
      subscription?.fetchChargeableQuantity().then(response => {
        setServiceLocations(response);
      });
      subscription?.fetchCustomerNumber().then(response => {
        setCustomerId(response);
      });
    }
  }, [state.subscriptionId]);

  useEffect(() => {
    if (state.subscriptionId && customerId) {
      const bill = new Bill(state.subscriptionId, customerId);
      const subscription = new Subscription(state.subscriptionId);
      bill?.fetchMonthlyBill().then(response => {
        setMonthlyBill(response);
      });

      bill?.fetchBillingCycleDate().then(response => {
        subscription?.fetchBillingCycleTime(response).then(response => {
          subscription?.fetchPaymentPlan(response).then(response => {
            setPaymentPlan(response);
          });
        });
      });
    }
  }, [customerId]);

  return (
    <>
      {isLoading && <PageLoading />}
      <Page as='main' minWidth='100%' data-test-manage-subscription>
        <TopNav />
        <PlanInformation
          enrollment={state.enrollment}
          isLogoApproved={state.isLogoApproved}
          handleEnrollmentUpdate={handleEnrollmentUpdate}
          setLoading={setLoading}
        />
        <PaymentInformation
          enrollment={state.enrollment}
          paymentSources={state.paymentSources}
          billingId={state.billingId}
          handleEnrollmentUpdate={handleEnrollmentUpdate}
          inactive={state.inactive}
          setLoading={setLoading}
          viewer={state.viewer}
          paymentPlan={paymentPlan}
          serviceLocations={serviceLocations}
          monthlyBill={monthlyBill}
        />
        <BillingHistory
          billingHistory={state.billingHistory}
          subscriptionId={state.subscriptionId}
        />
        {/* Available Upgrades is out for MVO */}
        {/* <AvailableUpgrades /> */}
      </Page>
    </>
  );
};

export default ManageSubscription;
